<template>
  <div
    class="damage-inspection mb-4 bg-secondary-subtle"
    :style="containerStyle"
  >
    <!-- Main title -->
    <h5 class="text-center mb-3" :style="titleStyle">DAMAGE INSPECTION</h5>

    <!-- OUT section -->
    <div class="mb-4">
      <div class="text-center mb-3" :style="sectionTitleStyle">OUT</div>
      <div class="row g-3">
        <div v-for="view in outViews" :key="view.id" :class="view.colClass">
          <div class="damage-view">
            <div class="text-center mb-2" :style="viewHeaderStyle">
              {{ view.title }}
            </div>
            <canvas
              :id="view.canvasId"
              :ref="view.canvasId"
              :data-view="view.viewType"
              :data-field="view.fieldName"
              :width="view.width"
              :height="view.height"
              :style="view.canvasStyle"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseout="stopDrawing"
              @touchstart="handleTouch"
              @touchmove="handleTouch"
              @touchend="stopDrawing"
            ></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- IN section -->
    <div class="mb-3">
      <div class="text-center mb-3" :style="sectionTitleStyle">IN</div>
      <div class="row g-3">
        <div v-for="view in inViews" :key="view.id" :class="view.colClass">
          <div class="damage-view">
            <div class="text-center mb-2" :style="viewHeaderStyle">
              {{ view.title }}
            </div>
            <canvas
              :id="view.canvasId"
              :ref="view.canvasId"
              :data-view="view.viewType"
              :data-field="view.fieldName"
              :width="view.width"
              :height="view.height"
              :style="view.canvasStyle"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseout="stopDrawing"
              @touchstart="handleTouch"
              @touchmove="handleTouch"
              @touchend="stopDrawing"
            ></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Controls -->
    <div class="text-end">
      <button
        type="button"
        class="btn btn-sm btn-secondary"
        @click="clearAllCanvases"
      >
        Clear
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from "vue";
import eventBus from "@apexl/icarus-core/src/services/eventBus";

const props = defineProps({
  existingDamage: {
    type: Object,
    default: () => ({}),
  },
  fieldPrefix: {
    type: String,
    default: "damage",
  },
});

// Drawing state
const drawingColor = "#dc3545"; // red
const isDrawing = ref(false);
const currentCanvas = ref(null);

// Damage data storage - sync with FormKit model
const damageData = reactive({
  damage_nearside_out: props.existingDamage.damage_nearside_out || "",
  damage_offside_out: props.existingDamage.damage_offside_out || "",
  damage_front_out: props.existingDamage.damage_front_out || "",
  damage_rear_out: props.existingDamage.damage_rear_out || "",
  damage_nearside_in: props.existingDamage.damage_nearside_in || "",
  damage_offside_in: props.existingDamage.damage_offside_in || "",
  damage_front_in: props.existingDamage.damage_front_in || "",
  damage_rear_in: props.existingDamage.damage_rear_in || "",
});

// View configurations
const outViews = [
  {
    id: "nearside-out",
    canvasId: "damage-canvas-nearside-out",
    fieldName: "damage_nearside_out",
    title: "NEARSIDE",
    viewType: "nearside",
    width: 200,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
  {
    id: "offside-out",
    canvasId: "damage-canvas-offside-out",
    fieldName: "damage_offside_out",
    title: "OFFSIDE",
    viewType: "offside",
    width: 200,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
  {
    id: "front-out",
    canvasId: "damage-canvas-front-out",
    fieldName: "damage_front_out",
    title: "FRONT",
    viewType: "front",
    width: 120,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
  {
    id: "rear-out",
    canvasId: "damage-canvas-rear-out",
    fieldName: "damage_rear_out",
    title: "REAR",
    viewType: "rear",
    width: 120,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
];

const inViews = [
  {
    id: "nearside-in",
    canvasId: "damage-canvas-nearside-in",
    fieldName: "damage_nearside_in",
    title: "NEARSIDE",
    viewType: "nearside",
    width: 200,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
  {
    id: "offside-in",
    canvasId: "damage-canvas-offside-in",
    fieldName: "damage_offside_in",
    title: "OFFSIDE",
    viewType: "offside",
    width: 200,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
  {
    id: "front-in",
    canvasId: "damage-canvas-front-in",
    fieldName: "damage_front_in",
    title: "FRONT",
    viewType: "front",
    width: 120,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
  {
    id: "rear-in",
    canvasId: "damage-canvas-rear-in",
    fieldName: "damage_rear_in",
    title: "REAR",
    viewType: "rear",
    width: 120,
    height: 120,
    colClass: "col-lg-3 col-6",
    canvasStyle:
      "border: 1px solid #6c757d; background: #f8f9fa; cursor: crosshair;",
  },
];

// Styles
const containerStyle =
  "border: 1px solid #dee2e6; border-radius: 8px; padding: 20px;";
const titleStyle =
  "background: #6c757d; color: white; padding: 8px; margin: -20px -20px 20px -20px;";
const sectionTitleStyle =
  "background: #343a40; color: white; padding: 8px; font-weight: bold;";
const viewHeaderStyle =
  "background: #495057; color: white; padding: 4px; font-size: 0.875rem; font-weight: bold;";

onMounted(async () => {
  await nextTick();
  initializeCanvases();
});

function initializeCanvases() {
  const allViews = [...outViews, ...inViews];

  allViews.forEach((view) => {
    const canvas = document.getElementById(view.canvasId);
    if (canvas) {
      setupCanvas(canvas, view);
    }
  });
}

function setupCanvas(canvas, view) {
  const ctx = canvas.getContext("2d");
  const fieldName = view.fieldName;

  // Load existing damage data if available
  const existingData = damageData[fieldName];
  if (existingData && existingData.startsWith("data:image/")) {
    const img = new Image();
    img.onload = function () {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = existingData;
  } else {
    // Draw fresh trailer outline
    drawTrailerView(ctx, canvas.width, canvas.height, view.viewType);
    // emit initial empty state to event bus for Form component
    saveCanvasData(canvas);
  }
}

function getMousePos(e, canvas) {
  const rect = canvas.getBoundingClientRect();
  return {
    x: (e.clientX - rect.left) * (canvas.width / rect.width),
    y: (e.clientY - rect.top) * (canvas.height / rect.height),
  };
}

function startDrawing(e) {
  e.preventDefault();
  const canvas = e.target;
  const ctx = canvas.getContext("2d");

  isDrawing.value = true;
  currentCanvas.value = canvas;

  const pos = getMousePos(e, canvas);

  ctx.beginPath();
  ctx.moveTo(pos.x, pos.y);
  ctx.strokeStyle = drawingColor;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
}

function draw(e) {
  if (!isDrawing.value || !currentCanvas.value) return;
  e.preventDefault();

  const canvas = e.target;
  if (canvas !== currentCanvas.value) return;

  const ctx = canvas.getContext("2d");
  const pos = getMousePos(e, canvas);

  ctx.lineTo(pos.x, pos.y);
  ctx.stroke();

  saveCanvasData(canvas);
}

function stopDrawing(e) {
  if (!isDrawing.value) return;

  isDrawing.value = false;
  const canvas = currentCanvas.value;
  if (canvas) {
    const ctx = canvas.getContext("2d");
    ctx.beginPath();
  }
  currentCanvas.value = null;
}

function handleTouch(e) {
  e.preventDefault();
  const touch = e.touches[0];
  const mouseEvent = new MouseEvent(
    e.type === "touchstart"
      ? "mousedown"
      : e.type === "touchmove"
      ? "mousemove"
      : "mouseup",
    {
      clientX: touch.clientX,
      clientY: touch.clientY,
    },
  );
  e.target.dispatchEvent(mouseEvent);
}

function saveCanvasData(canvas) {
  const canvasData = canvas.toDataURL();
  const fieldName = canvas.getAttribute("data-field");

  if (fieldName && damageData.hasOwnProperty(fieldName)) {
    damageData[fieldName] = canvasData;

    // Emit to global event bus for Form component to pick up
    eventBus.$emit("fieldUpdated", {
      fieldName: fieldName,
      value: canvasData,
    });
  }
}

function clearAllCanvases() {
  const allViews = [...outViews, ...inViews];
  const clearedFieldNames = [];

  allViews.forEach((view) => {
    const canvas = document.getElementById(view.canvasId);
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      drawTrailerView(ctx, canvas.width, canvas.height, view.viewType);

      // Clear damage data
      damageData[view.fieldName] = "";
      clearedFieldNames.push(view.fieldName);

      // Emit individual field updates to event bus
      eventBus.$emit("fieldUpdated", {
        fieldName: view.fieldName,
        value: "",
      });
    }
  });

  console.log("All damage fields cleared:", clearedFieldNames);
}

function drawTrailerView(ctx, width, height, viewType) {
  ctx.strokeStyle = "#666";
  ctx.lineWidth = 1.5;
  ctx.fillStyle = "#f8f9fa";

  if (viewType === "nearside") {
    drawSideView(ctx, width, height, true);
  } else if (viewType === "offside") {
    drawSideView(ctx, width, height, false);
  } else if (viewType === "front") {
    drawFrontView(ctx, width, height);
  } else if (viewType === "rear") {
    drawRearView(ctx, width, height);
  }
}

function drawSideView(ctx, width, height, isNearside = true) {
  const margin = 15;
  const trailerHeight = height * 0.5;
  const trailerY = height * 0.25;
  const trailerWidth = width - margin * 2;

  // Main trailer body
  ctx.fillRect(margin, trailerY, trailerWidth, trailerHeight);
  ctx.strokeRect(margin, trailerY, trailerWidth, trailerHeight);

  // Draw hitch/coupling at front (near side) or rear (off side)
  const hitchX = isNearside ? margin - 8 : margin + trailerWidth;
  ctx.fillRect(hitchX, trailerY + trailerHeight * 0.4, 8, trailerHeight * 0.2);
  ctx.strokeRect(
    hitchX,
    trailerY + trailerHeight * 0.4,
    8,
    trailerHeight * 0.2,
  );

  // Draw wheels (3 axles typical)
  const wheelRadius = 6;
  const wheelY = trailerY + trailerHeight + wheelRadius + 3;
  const axlePositions = isNearside
    ? [width * 0.70, width * 0.77, width * 0.84]
    : [width * 0.30, width * 0.23, width * 0.16];

  ctx.fillStyle = "#333";
  axlePositions.forEach((x) => {
    ctx.beginPath();
    ctx.arc(x, wheelY, wheelRadius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
  });

  // Panel lines
  ctx.strokeStyle = "#999";
  ctx.lineWidth = 1;
  [width * 0.25, width * 0.5, width * 0.75].forEach((x) => {
    ctx.beginPath();
    ctx.moveTo(x, trailerY);
    ctx.lineTo(x, trailerY + trailerHeight);
    ctx.stroke();
  });
}

function drawFrontView(ctx, width, height) {
  const margin = 10;
  const trailerWidth = width - margin * 2;
  const trailerHeight = height * 0.65;
  const trailerY = height * 0.15;

  // Main trailer body
  ctx.fillRect(margin, trailerY, trailerWidth, trailerHeight);
  ctx.strokeRect(margin, trailerY, trailerWidth, trailerHeight);

  // Center divider
  const centerX = margin + trailerWidth / 2;
  ctx.strokeStyle = "#999";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(centerX, trailerY);
  ctx.lineTo(centerX, trailerY + trailerHeight);
  ctx.stroke();

  // Door handles
  ctx.fillStyle = "#333";
  const handleY = trailerY + trailerHeight * 0.5;
  ctx.beginPath();
  ctx.arc(centerX - 15, handleY, 2, 0, 2 * Math.PI);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(centerX + 15, handleY, 2, 0, 2 * Math.PI);
  ctx.fill();

  // Wheels at bottom
  const wheelRadius = 5;
  const wheelY = trailerY + trailerHeight + wheelRadius + 3;
  const leftWheelX = margin + trailerWidth * 0.2;
  const rightWheelX = margin + trailerWidth * 0.8;

  ctx.beginPath();
  ctx.arc(leftWheelX, wheelY, wheelRadius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(rightWheelX, wheelY, wheelRadius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
}

function drawRearView(ctx, width, height) {
  const margin = 10;
  const trailerWidth = width - margin * 2;
  const trailerHeight = height * 0.65;
  const trailerY = height * 0.15;

  // Main trailer body
  ctx.fillRect(margin, trailerY, trailerWidth, trailerHeight);
  ctx.strokeRect(margin, trailerY, trailerWidth, trailerHeight);

  // Rear doors
  const centerX = margin + trailerWidth / 2;
  ctx.strokeStyle = "#999";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(centerX, trailerY);
  ctx.lineTo(centerX, trailerY + trailerHeight);
  ctx.stroke();

  // Door latches/handles
  ctx.strokeStyle = "#333";
  ctx.lineWidth = 2;
  const handleY = trailerY + trailerHeight * 0.9;
  ctx.beginPath();
  ctx.rect(centerX - 8, handleY - 4, 16, 8);
  ctx.stroke();

  // Rear lights
  ctx.fillStyle = "#ff4444";
  const lightY = trailerY + trailerHeight - 8;
  ctx.beginPath();
  ctx.arc(margin + 8, lightY, 3, 0, 2 * Math.PI);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(width - margin - 8, lightY, 3, 0, 2 * Math.PI);
  ctx.fill();

  // Wheels
  const wheelRadius = 5;
  const wheelY = trailerY + trailerHeight + wheelRadius + 3;
  const leftWheelX = margin + trailerWidth * 0.2;
  const rightWheelX = margin + trailerWidth * 0.8;

  ctx.fillStyle = "#333";
  ctx.beginPath();
  ctx.arc(leftWheelX, wheelY, wheelRadius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(rightWheelX, wheelY, wheelRadius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
}
</script>

<style scoped>
.damage-inspection canvas {
  display: block;
  margin: 0 auto;
}

.damage-view {
  text-align: center;
}

.row {
  margin: 0;
}
</style>
